import React from 'react';
import { Link } from 'react-router-dom';
import {
  BuildingOfficeIcon,
  PlusIcon,
  ListBulletIcon,
  ArrowRightOnRectangleIcon,
  CalendarIcon,
  ClockIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';

const Acente_Anasayfa = ({ user }) => {
  const currentDate = new Date();
  const formattedDate = currentDate.toLocaleDateString('tr-TR', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  const formattedTime = currentDate.toLocaleTimeString('tr-TR', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });

  const dashboardCards = [
    {
      title: 'Talep Oluştur',
      description: 'Yeni transfer talepleri oluşturun ve sistemimize gönderin',
      icon: PlusIcon,
      href: '/agency/talep-olustur',
      color: 'from-red-500 to-red-600',
      iconColor: 'text-red-600',
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200',
      hoverColor: 'hover:border-red-300',
      gradient: 'bg-gradient-to-br from-red-50 to-red-100'
    },
    {
      title: 'Taleplerim',
      description: 'Gönderdiğiniz talepleri görüntüleyin ve durumlarını takip edin',
      icon: ListBulletIcon,
      href: '/agency/taleplerim',
      color: 'from-emerald-500 to-emerald-600',
      iconColor: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
      borderColor: 'border-emerald-200',
      hoverColor: 'hover:border-emerald-300',
      gradient: 'bg-gradient-to-br from-emerald-50 to-emerald-100'
    },
    {
      title: 'Raporlar',
      description: 'Transfer geçmişinizi ve performans raporlarınızı inceleyin',
      icon: ChartBarIcon,
      href: '/agency/raporlar',
      color: 'from-violet-500 to-violet-600',
      iconColor: 'text-violet-600',
      bgColor: 'bg-violet-50',
      borderColor: 'border-violet-200',
      hoverColor: 'hover:border-violet-300',
      gradient: 'bg-gradient-to-br from-violet-50 to-violet-100'
    }
  ];

  return (
    <div className="space-y-10">
      {/* Welcome Section */}
      <div className="text-center space-y-6">
        <div className="space-y-4">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-red-500 to-red-600 rounded-full shadow-lg">
            <BuildingOfficeIcon className="h-10 w-10 text-white" />
          </div>
          <h1 className="text-5xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
            Hoş Geldiniz!
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
            {user?.agency_name || 'Acente'} - BHT Clinic Transfer Sistemi'ne hoş geldiniz. 
            Transfer taleplerinizi kolayca yönetin ve takip edin.
          </p>
        </div>
        
        {/* Enhanced Date/Time */}
        <div className="max-w-lg mx-auto">
          <div className="space-y-4">
            <div className="flex items-center justify-center gap-3 text-gray-700">
              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white shadow-sm">
                <CalendarIcon className="h-5 w-5 text-gray-600" />
                <span className="font-semibold text-gray-800">{formattedDate}</span>
              </div>
            </div>
            <div className="flex items-center justify-center gap-3 text-gray-700">
              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white shadow-sm">
                <ClockIcon className="h-5 w-5 text-gray-600" />
                <span className="font-semibold text-gray-800">{formattedTime}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Action Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {dashboardCards.map((card, index) => (
          <Link
            key={card.title}
            to={card.href}
            className={`group relative overflow-hidden ${card.gradient} rounded-xl p-6 transition-all duration-300 hover:scale-105 hover:shadow-xl border-2 ${card.borderColor} ${card.hoverColor}`}
            style={{
              animationDelay: `${index * 100}ms`
            }}
          >
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-5">
              <div className="absolute top-0 right-0 w-20 h-20 bg-white rounded-full -translate-y-10 translate-x-10"></div>
              <div className="absolute bottom-0 left-0 w-16 h-16 bg-white rounded-full translate-y-8 -translate-x-8"></div>
            </div>
            
            <div className="relative z-10">
              {/* Icon */}
              <div className="flex items-center justify-center w-12 h-12 bg-white/80 backdrop-blur-sm rounded-xl mb-4 shadow-md">
                <card.icon className={`h-6 w-6 ${card.iconColor}`} />
              </div>
              
              {/* Content */}
              <div className="space-y-3">
                <h3 className="text-lg font-bold text-gray-800 group-hover:text-gray-900 transition-colors">
                  {card.title}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed group-hover:text-gray-700 transition-colors">
                  {card.description}
                </p>
                
                {/* Action Button */}
                <div className="pt-2">
                  <div className={`inline-flex items-center px-4 py-2 bg-gradient-to-r ${card.color} text-white rounded-lg text-xs font-semibold shadow-md group-hover:shadow-lg transition-all duration-300 group-hover:scale-105`}>
                    Başla
                    <ArrowRightOnRectangleIcon className="h-3 w-3 ml-2 group-hover:translate-x-1 transition-transform duration-300" />
                  </div>
                </div>
              </div>
            </div>
            
            {/* Hover Effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          </Link>
        ))}
      </div>

    </div>
  );
};

export default Acente_Anasayfa;
